import { cva, type VariantProps } from 'class-variance-authority'
import { type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { AlertTriangle, XCircle } from 'lucide-react'
import { useCellContext } from './context'

function CheckCircle({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_1102_12627)">
        <path
          d="M7.99805 0.664062C12.0479 0.664239 15.3309 3.94822 15.3311 7.99805C15.3309 12.0479 12.0479 15.3309 7.99805 15.3311C3.94822 15.3309 0.664239 12.0479 0.664062 7.99805C0.664238 3.94822 3.94822 0.664238 7.99805 0.664062ZM10.4697 6.19336C10.2094 5.93301 9.78672 5.93301 9.52637 6.19336L7.33105 8.38867L6.46973 7.52734C6.20938 7.26699 5.78672 7.26699 5.52637 7.52734C5.26656 7.78768 5.26634 8.20953 5.52637 8.46973L6.86035 9.80273C7.12066 10.0629 7.54243 10.0629 7.80273 9.80273L10.4697 7.13672C10.73 6.87642 10.7299 6.45372 10.4697 6.19336Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_1102_12627">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

const cellBannerVariants = cva('flex w-full flex-wrap items-center border rounded-lg corner-smooth text-sm', {
  variants: {
    variant: {
      success: 'bg-success-subtle text-success-subtle border-success',
      warning: 'bg-warning-subtle text-warning-subtle border-warning',
      danger: 'bg-danger-subtle text-danger-subtle border-danger',
    },
    size: {
      default: 'p-3 mt-3 gap-2',
      compact: 'p-2 mt-2 gap-1.5',
    },
  },
  defaultVariants: {
    variant: 'success',
    size: 'default',
  },
})

type CellBannerProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof cellBannerVariants> & {
    action?: ReactNode
  }

function CellBanner({ className, children, variant, action, ...props }: CellBannerProps) {
  const { size } = useCellContext()

  return (
    <div
      data-slot="cell-banner"
      className={twMerge(cellBannerVariants({ variant, size }), className)}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      <span className="[&_svg]:size-4">
        {variant === 'success' ? <CheckCircle /> : variant === 'warning' ? <AlertTriangle /> : <XCircle />}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
      {action && <span className="ml-auto">{action}</span>}
    </div>
  )
}

export { CellBanner }
